import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaStar, FaCheck, FaTimes, FaArrowRight } from 'react-icons/fa';
import SEO from '../components/SEO';

const Comparison: React.FC = () => {
  const [selectedFeature, setSelectedFeature] = useState<string | null>(null);

  const offers = [
    {
      id: 'allbritishcasino',
      name: 'All British Casino',
      image: '/images/offers/allbritishcasino.webp',
      bonus: '100% up to £100',
      rating: 4.8,
      minDeposit: '£10',
      wagering: '35x',
      payoutTime: '24-48h',
      license: 'UKGC',
      games: 500,
      mobile: true,
      liveChat: true,
      crypto: false,
      vip: true
    },
    {
      id: 'partycasino',
      name: 'PartyCasino',
      image: '/images/offers/partycasino.webp',
      bonus: '200% up to £200',
      rating: 4.7,
      minDeposit: '£20',
      wagering: '30x',
      payoutTime: '12-24h',
      license: 'UKGC',
      games: 800,
      mobile: true,
      liveChat: true,
      crypto: true,
      vip: true
    },
    {
      id: 'plazaroyal',
      name: 'Plaza Royal',
      image: '/images/offers/plazaroyal.webp',
      bonus: '150% up to £150',
      rating: 4.6,
      minDeposit: '£15',
      wagering: '40x',
      payoutTime: '24-72h',
      license: 'UKGC',
      games: 600,
      mobile: true,
      liveChat: true,
      crypto: true,
      vip: false
    },
  ];

  const features = [
    { key: 'bonus', label: 'Welcome Bonus' },
    { key: 'rating', label: 'Rating' },
    { key: 'minDeposit', label: 'Min Deposit' },
    { key: 'wagering', label: 'Wagering' },
    { key: 'payoutTime', label: 'Payout Time' },
    { key: 'games', label: 'Games' },
    { key: 'mobile', label: 'Mobile App' },
    { key: 'liveChat', label: '24/7 Support' },
    { key: 'crypto', label: 'Crypto Accepted' },
    { key: 'vip', label: 'VIP Program' },
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Casino Offer Comparison",
    "description": "Compare the best UK casino offers side by side. Find the perfect casino match with our detailed comparison tool."
  };

  return (
    <>
      <SEO
        title="Compare UK Casino Offers - Side by Side Comparison"
        description="Compare the best UK casino offers, bonuses, and features side by side. Find your perfect casino match with our comprehensive comparison tool."
        schema={schema}
      />

      <div className="pt-20 pb-12">
        <section className="py-12 bg-gradient-to-br from-dark-surface to-dark-bg">
          <div className="container mx-auto px-4">
            <h1 className="text-5xl md:text-6xl font-bold text-center mb-4">
              <span className="text-gradient">Compare Casino Offers</span>
            </h1>
            <p className="text-xl text-gray-400 text-center max-w-3xl mx-auto">
              Side-by-side comparison of the UK's top casino offers. Find the best match for your gaming preferences.
            </p>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="glass rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-dark-card">
                    <tr>
                      <th className="px-6 py-4 text-left font-bold">Feature</th>
                      {offers.map((offer) => (
                        <th key={offer.id} className="px-6 py-4 text-center">
                          <div className="flex flex-col items-center">
                            <img
                              src={offer.image}
                              alt={offer.name}
                              className="w-20 h-20 object-contain rounded-lg mb-2"
                              loading="lazy"
                            />
                            <span className="font-bold">{offer.name}</span>
                            <div className="flex items-center gap-1 mt-1">
                              <FaStar className="text-accent-primary" size={14} />
                              <span className="text-sm">{offer.rating}</span>
                            </div>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {features.map((feature, idx) => (
                      <tr
                        key={feature.key}
                        className={`border-t border-dark-border hover:bg-dark-hover transition-colors ${
                          selectedFeature === feature.key ? 'bg-dark-hover' : ''
                        }`}
                        onClick={() => setSelectedFeature(feature.key)}
                      >
                        <td className="px-6 py-4 font-semibold">{feature.label}</td>
                        {offers.map((offer) => (
                          <td key={offer.id} className="px-6 py-4 text-center">
                            {typeof offer[feature.key as keyof typeof offer] === 'boolean' ? (
                              offer[feature.key as keyof typeof offer] ? (
                                <FaCheck className="text-green-400 mx-auto" size={20} />
                              ) : (
                                <FaTimes className="text-red-400 mx-auto" size={20} />
                              )
                            ) : (
                              <span className="text-gray-300">
                                {offer[feature.key as keyof typeof offer]}
                              </span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              {offers.map((offer) => (
                <Link
                  key={offer.id}
                  to={`/offers/${offer.id}`}
                  className="glass p-6 rounded-xl hover:border-accent-primary transition-all transform hover:scale-105 group"
                >
                  <div className="flex items-center justify-between mb-4">
                    <img
                      src={offer.image}
                      alt={offer.name}
                      className="w-16 h-16 object-contain rounded-lg"
                      loading="lazy"
                    />
                    <FaArrowRight className="text-accent-primary group-hover:translate-x-2 transition-transform" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{offer.name}</h3>
                  <p className="text-accent-primary font-semibold mb-4">{offer.bonus}</p>
                  <div className="flex items-center gap-2">
                    <div className="flex text-accent-primary">
                      {[...Array(5)].map((_, i) => (
                        <FaStar
                          key={i}
                          className={i < Math.floor(offer.rating) ? 'fill-current' : 'text-gray-600'}
                          size={14}
                        />
                      ))}
                    </div>
                    <span className="text-gray-400 text-sm">{offer.rating}/5</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Tips Section */}
        <section className="py-12 bg-dark-surface">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-4xl font-bold mb-8 text-center">
              <span className="text-gradient">Comparison Tips</span>
            </h2>
            <div className="space-y-6">
              <div className="glass p-6 rounded-xl">
                <h3 className="text-xl font-bold mb-3">Consider Your Priorities</h3>
                <p className="text-gray-400">
                  Think about what matters most to you: bonus size, game selection, payout speed, or customer support. 
                  Use this comparison to find casinos that excel in your priority areas.
                </p>
              </div>
              <div className="glass p-6 rounded-xl">
                <h3 className="text-xl font-bold mb-3">Read the Terms</h3>
                <p className="text-gray-400">
                  Always check wagering requirements, minimum deposits, and withdrawal limits before claiming any bonus. 
                  The best bonus isn't always the biggest one if the terms are unfavorable.
                </p>
              </div>
              <div className="glass p-6 rounded-xl">
                <h3 className="text-xl font-bold mb-3">Check Licensing</h3>
                <p className="text-gray-400">
                  All casinos in our comparison are UKGC licensed, ensuring they meet strict regulatory standards. 
                  This guarantees fair play, secure transactions, and responsible gaming practices.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Comparison;

